import { Language } from "@/contexts/LanguageContext";

export interface IndustryTranslation {
  title: string;
  description: string;
  products: string[];
  tasks: string[];
  solutions: string[];
  suppliers: { category: string; brands: string }[];
  advantages: string[];
}

type IndustryTranslations = Record<string, Record<Language, IndustryTranslation>>;

export const industryTranslations: IndustryTranslations = {
  "automotive-ev": {
    en: {
      title: "Automotive & EV",
      description: "Comprehensive supply of industrial components for OEM manufacturers and their tier suppliers. Fast automation solutions for traditional automotive plants and modern EV production.",
      products: [
        "Industrial PCs and HMI panels",
        "PLC systems and sensors for robotics",
        "Machine vision and inline inspection systems",
        "Cleanroom and battery-production equipment",
      ],
      tasks: [
        "Automation of assembly lines",
        "Robotic welding and painting",
        "Quality control and testing of assembled modules",
        "Battery cell and pack production processes",
      ],
      solutions: [
        "Industrial PCs and HMI panels",
        "PLC systems and sensors for robotics",
        "Machine vision systems",
        "Cleanroom equipment for battery lines",
      ],
      suppliers: [
        { category: "Automation", brands: "Siemens, Rockwell Automation, Schneider Electric, Beckhoff" },
        { category: "Robotics", brands: "KUKA, ABB, FANUC, Yaskawa" },
        { category: "Quality control", brands: "Cognex, Keyence, Sick" },
        { category: "Sensors & Instrumentation", brands: "IFM Electronic, Pepperl+Fuchs, Balluff" },
      ],
      advantages: [
        "Fast delivery for urgent repairs and line upgrades",
        "Tailored terms for niche OEMs and suppliers",
        "Minimal bureaucracy — quick approval and dispatch",
        "Turnkey approach from sensor to control system",
      ],
    },
    de: {
      title: "Automobil & E-Mobilität",
      description: "Umfassende Versorgung mit Industriekomponenten für OEM-Hersteller und ihre Tier-Zulieferer. Schnelle Automatisierungslösungen für traditionelle Automobilwerke und moderne EV-Produktion.",
      products: [
        "Industrie-PCs und HMI-Panels",
        "SPS-Systeme und Sensoren für Robotik",
        "Bildverarbeitungs- und Inline-Inspektionssysteme",
        "Reinraum- und Batteriefertigungsausrüstung",
      ],
      tasks: [
        "Automatisierung von Montagelinien",
        "Roboterschweißen und -lackieren",
        "Qualitätskontrolle und Prüfung montierter Module",
        "Batteriezellen- und Packfertigungsprozesse",
      ],
      solutions: [
        "Industrie-PCs und HMI-Panels",
        "SPS-Systeme und Sensoren für Robotik",
        "Bildverarbeitungssysteme",
        "Reinraumausrüstung für Batterielinien",
      ],
      suppliers: [
        { category: "Automatisierung", brands: "Siemens, Rockwell Automation, Schneider Electric, Beckhoff" },
        { category: "Robotik", brands: "KUKA, ABB, FANUC, Yaskawa" },
        { category: "Qualitätskontrolle", brands: "Cognex, Keyence, Sick" },
        { category: "Sensoren & Instrumentierung", brands: "IFM Electronic, Pepperl+Fuchs, Balluff" },
      ],
      advantages: [
        "Schnelle Lieferung für dringende Reparaturen und Linienupgrades",
        "Maßgeschneiderte Konditionen für Nischen-OEMs und Lieferanten",
        "Minimale Bürokratie — schnelle Genehmigung und Versand",
        "Schlüsselfertiger Ansatz vom Sensor bis zum Steuerungssystem",
      ],
    },
    es: {
      title: "Automotriz & Vehículos Eléctricos",
      description: "Suministro integral de componentes industriales para fabricantes OEM y sus proveedores de nivel. Soluciones de automatización rápidas para plantas automotrices tradicionales y producción moderna de vehículos eléctricos.",
      products: [
        "PCs industriales y paneles HMI",
        "Sistemas PLC y sensores para robótica",
        "Sistemas de visión artificial e inspección en línea",
        "Equipos de sala limpia y producción de baterías",
      ],
      tasks: [
        "Automatización de líneas de montaje",
        "Soldadura y pintura robótica",
        "Control de calidad y pruebas de módulos ensamblados",
        "Procesos de producción de celdas y paquetes de baterías",
      ],
      solutions: [
        "PCs industriales y paneles HMI",
        "Sistemas PLC y sensores para robótica",
        "Sistemas de visión artificial",
        "Equipos de sala limpia para líneas de baterías",
      ],
      suppliers: [
        { category: "Automatización", brands: "Siemens, Rockwell Automation, Schneider Electric, Beckhoff" },
        { category: "Robótica", brands: "KUKA, ABB, FANUC, Yaskawa" },
        { category: "Control de calidad", brands: "Cognex, Keyence, Sick" },
        { category: "Sensores e Instrumentación", brands: "IFM Electronic, Pepperl+Fuchs, Balluff" },
      ],
      advantages: [
        "Entrega rápida para reparaciones urgentes y actualizaciones de línea",
        "Términos personalizados para OEMs y proveedores de nicho",
        "Mínima burocracia — aprobación y envío rápidos",
        "Enfoque llave en mano desde el sensor hasta el sistema de control",
      ],
    },
  },
  "renewable-energy": {
    en: {
      title: "Renewable Energy",
      description: "Rapid delivery of automation solutions for renewable energy facilities. Support for urgent orders to minimize downtime.",
      products: [
        "Industrial controllers and RTUs",
        "Vibration, pressure and environmental sensors",
        "Explosion- and weather-proof equipment",
        "SCADA systems and remote telemetry",
      ],
      tasks: [
        "Wind turbine control and pitch systems",
        "Solar farm performance monitoring and inverter control",
        "Automation for hydrogen production and electrolysers",
        "Centralized energy park SCADA and grid interface",
      ],
      solutions: [
        "Industrial controllers and RTUs",
        "Vibration and pressure sensors",
        "Explosion-proof and weather-proof enclosures",
        "SCADA systems and telemetry",
      ],
      suppliers: [
        { category: "Automation", brands: "Siemens, Schneider Electric, Emerson, WAGO" },
        { category: "Energy systems", brands: "Bachmann, Honeywell" },
        { category: "Sensors & Instrumentation", brands: "Endress+Hauser, Vega, KROHNE, ifm electronic" },
        { category: "Safety", brands: "Pepperl+Fuchs, R. STAHL" },
      ],
      advantages: [
        "Urgent deliveries for critical assets",
        "Custom orders without excessive approvals",
        "24/7 technical support for emergency cases",
        "Experience integrating heterogeneous systems",
      ],
    },
    de: {
      title: "Erneuerbare Energien",
      description: "Schnelle Lieferung von Automatisierungslösungen für Anlagen erneuerbarer Energien. Unterstützung für dringende Bestellungen zur Minimierung von Ausfallzeiten.",
      products: [
        "Industrieregler und RTUs",
        "Vibrations-, Druck- und Umweltsensoren",
        "Explosions- und wetterfeste Ausrüstung",
        "SCADA-Systeme und Ferntelemetrie",
      ],
      tasks: [
        "Windturbinensteuerung und Pitch-Systeme",
        "Leistungsüberwachung von Solarparks und Wechselrichtersteuerung",
        "Automatisierung für Wasserstoffproduktion und Elektrolyseure",
        "Zentrales Energiepark-SCADA und Netzschnittstelle",
      ],
      solutions: [
        "Industrieregler und RTUs",
        "Vibrations- und Drucksensoren",
        "Explosions- und wetterfeste Gehäuse",
        "SCADA-Systeme und Telemetrie",
      ],
      suppliers: [
        { category: "Automatisierung", brands: "Siemens, Schneider Electric, Emerson, WAGO" },
        { category: "Energiesysteme", brands: "Bachmann, Honeywell" },
        { category: "Sensoren & Instrumentierung", brands: "Endress+Hauser, Vega, KROHNE, ifm electronic" },
        { category: "Sicherheit", brands: "Pepperl+Fuchs, R. STAHL" },
      ],
      advantages: [
        "Dringende Lieferungen für kritische Anlagen",
        "Sonderbestellungen ohne übermäßige Genehmigungen",
        "24/7 technischer Support für Notfälle",
        "Erfahrung in der Integration heterogener Systeme",
      ],
    },
    es: {
      title: "Energías Renovables",
      description: "Entrega rápida de soluciones de automatización para instalaciones de energía renovable. Soporte para pedidos urgentes para minimizar tiempos de inactividad.",
      products: [
        "Controladores industriales y RTUs",
        "Sensores de vibración, presión y ambientales",
        "Equipos a prueba de explosiones y clima",
        "Sistemas SCADA y telemetría remota",
      ],
      tasks: [
        "Control de turbinas eólicas y sistemas de pitch",
        "Monitoreo de rendimiento de parques solares y control de inversores",
        "Automatización para producción de hidrógeno y electrolizadores",
        "SCADA centralizado de parques energéticos e interfaz de red",
      ],
      solutions: [
        "Controladores industriales y RTUs",
        "Sensores de vibración y presión",
        "Gabinetes a prueba de explosiones y clima",
        "Sistemas SCADA y telemetría",
      ],
      suppliers: [
        { category: "Automatización", brands: "Siemens, Schneider Electric, Emerson, WAGO" },
        { category: "Sistemas energéticos", brands: "Bachmann, Honeywell" },
        { category: "Sensores e Instrumentación", brands: "Endress+Hauser, Vega, KROHNE, ifm electronic" },
        { category: "Seguridad", brands: "Pepperl+Fuchs, R. STAHL" },
      ],
      advantages: [
        "Entregas urgentes para activos críticos",
        "Pedidos personalizados sin aprobaciones excesivas",
        "Soporte técnico 24/7 para casos de emergencia",
        "Experiencia integrando sistemas heterogéneos",
      ],
    },
  },
  "food-beverage": {
    en: {
      title: "Food & Beverage",
      description: "Fast deliveries for food production plants with strict hygiene and traceability standards.",
      products: [
        "Hygienic valves, clamps and fittings",
        "Stainless-steel conveyor components",
        "Food-grade seals and gaskets",
        "Pumps and dosing systems for liquids",
      ],
      tasks: [
        "Automation of filling and packaging lines",
        "CIP (Clean-In-Place) and SIP system control",
        "Inline quality and parameter monitoring",
        "Product traceability and batch tracking",
      ],
      solutions: [
        "Hygienic sensors and controllers (food-grade)",
        "Hygienic-rated HMI panels and enclosures",
        "Stainless-steel components and fittings",
        "Cleaning, sterilization and CIP control systems",
      ],
      suppliers: [
        { category: "Automation", brands: "Siemens, Rockwell Automation, Schneider Electric" },
        { category: "Hygienic equipment", brands: "Endress+Hauser, GEA, SPX Flow, Alfa Laval" },
        { category: "Sensors & Instrumentation", brands: "ifm electronic, Vega, Balluff" },
        { category: "Process equipment", brands: "Krones, KHS" },
      ],
      advantages: [
        "Express deliveries to avoid production stops",
        "Substitution and match-making for unavailable OEM parts",
        "Compatibility consulting for hygienic systems",
        "Special-order supply for niche hygienic components",
      ],
    },
    de: {
      title: "Lebensmittel & Getränke",
      description: "Schnelle Lieferungen für Lebensmittelproduktionsanlagen mit strengen Hygiene- und Rückverfolgbarkeitsstandards.",
      products: [
        "Hygienische Ventile, Klemmen und Fittings",
        "Edelstahl-Förderkomponenten",
        "Lebensmitteltaugliche Dichtungen",
        "Pumpen und Dosiersysteme für Flüssigkeiten",
      ],
      tasks: [
        "Automatisierung von Abfüll- und Verpackungslinien",
        "CIP (Clean-In-Place) und SIP-Systemsteuerung",
        "Inline-Qualitäts- und Parameterüberwachung",
        "Produktrückverfolgbarkeit und Chargenverfolgung",
      ],
      solutions: [
        "Hygienische Sensoren und Regler (lebensmitteltauglich)",
        "Hygienisch zugelassene HMI-Panels und Gehäuse",
        "Edelstahlkomponenten und Fittings",
        "Reinigungs-, Sterilisations- und CIP-Steuerungssysteme",
      ],
      suppliers: [
        { category: "Automatisierung", brands: "Siemens, Rockwell Automation, Schneider Electric" },
        { category: "Hygienische Ausrüstung", brands: "Endress+Hauser, GEA, SPX Flow, Alfa Laval" },
        { category: "Sensoren & Instrumentierung", brands: "ifm electronic, Vega, Balluff" },
        { category: "Prozessausrüstung", brands: "Krones, KHS" },
      ],
      advantages: [
        "Expresslieferungen zur Vermeidung von Produktionsstopps",
        "Ersatz und Matching für nicht verfügbare OEM-Teile",
        "Kompatibilitätsberatung für hygienische Systeme",
        "Sonderbestellungsversorgung für Nischen-Hygienekomponenten",
      ],
    },
    es: {
      title: "Alimentos y Bebidas",
      description: "Entregas rápidas para plantas de producción de alimentos con estrictos estándares de higiene y trazabilidad.",
      products: [
        "Válvulas higiénicas, abrazaderas y accesorios",
        "Componentes de transportador de acero inoxidable",
        "Sellos y juntas de grado alimenticio",
        "Bombas y sistemas de dosificación para líquidos",
      ],
      tasks: [
        "Automatización de líneas de llenado y empaque",
        "Control de sistemas CIP (limpieza in situ) y SIP",
        "Monitoreo en línea de calidad y parámetros",
        "Trazabilidad de productos y seguimiento de lotes",
      ],
      solutions: [
        "Sensores y controladores higiénicos (grado alimenticio)",
        "Paneles HMI y gabinetes de grado higiénico",
        "Componentes y accesorios de acero inoxidable",
        "Sistemas de control de limpieza, esterilización y CIP",
      ],
      suppliers: [
        { category: "Automatización", brands: "Siemens, Rockwell Automation, Schneider Electric" },
        { category: "Equipos higiénicos", brands: "Endress+Hauser, GEA, SPX Flow, Alfa Laval" },
        { category: "Sensores e Instrumentación", brands: "ifm electronic, Vega, Balluff" },
        { category: "Equipos de proceso", brands: "Krones, KHS" },
      ],
      advantages: [
        "Entregas express para evitar paradas de producción",
        "Sustitución y coincidencia para piezas OEM no disponibles",
        "Consultoría de compatibilidad para sistemas higiénicos",
        "Suministro de pedidos especiales para componentes higiénicos de nicho",
      ],
    },
  },
  "pharmaceuticals-biotech": {
    en: {
      title: "Pharmaceuticals & Biotech",
      description: "Fast supply of validated equipment for pharmaceutical manufacturers with minimal downtime.",
      products: [
        "Sterile-grade valves and fittings",
        "Precision mechanical components",
        "Clean-room compatible machine parts",
        "Dosing pumps and filtration elements",
      ],
      tasks: [
        "Bioreactor automation and feed control",
        "Validation and calibration workflows",
        "Cleanroom environmental monitoring",
        "Purification and filtration system control",
      ],
      solutions: [
        "Validated PLCs and controllers",
        "Sterile-rated sensors and transmitters",
        "Environmental and process monitoring systems",
        "Cleanroom-grade equipment and enclosures",
      ],
      suppliers: [
        { category: "Automation", brands: "Siemens, Rockwell Automation, Emerson" },
        { category: "Process equipment", brands: "Endress+Hauser, Mettler Toledo, Sartorius" },
        { category: "Cleanroom systems", brands: "Camfil, Dexmet" },
        { category: "Quality control", brands: "Merck Millipore, Pall Corporation" },
      ],
      advantages: [
        "Priority shipping for critical production lines",
        "Short lead times for custom validated orders",
        "Full technical documentation for validation packages",
        "24/7 support for emergency and validation events",
      ],
    },
    de: {
      title: "Pharma & Biotechnologie",
      description: "Schnelle Lieferung validierter Ausrüstung für Pharmahersteller mit minimalen Ausfallzeiten.",
      products: [
        "Sterile Ventile und Fittings",
        "Präzisions-Mechanikkomponenten",
        "Reinraumkompatible Maschinenteile",
        "Dosierpumpen und Filtrationselemente",
      ],
      tasks: [
        "Bioreaktor-Automatisierung und Zuführungssteuerung",
        "Validierungs- und Kalibrierungsworkflows",
        "Reinraum-Umgebungsüberwachung",
        "Steuerung von Reinigungs- und Filtrationssystemen",
      ],
      solutions: [
        "Validierte SPSen und Controller",
        "Steril zugelassene Sensoren und Transmitter",
        "Umgebungs- und Prozessüberwachungssysteme",
        "Reinraumtaugliche Ausrüstung und Gehäuse",
      ],
      suppliers: [
        { category: "Automatisierung", brands: "Siemens, Rockwell Automation, Emerson" },
        { category: "Prozessausrüstung", brands: "Endress+Hauser, Mettler Toledo, Sartorius" },
        { category: "Reinraumsysteme", brands: "Camfil, Dexmet" },
        { category: "Qualitätskontrolle", brands: "Merck Millipore, Pall Corporation" },
      ],
      advantages: [
        "Prioritätsversand für kritische Produktionslinien",
        "Kurze Lieferzeiten für kundenspezifische validierte Bestellungen",
        "Vollständige technische Dokumentation für Validierungspakete",
        "24/7 Support für Notfall- und Validierungsereignisse",
      ],
    },
    es: {
      title: "Farmacéutica y Biotecnología",
      description: "Suministro rápido de equipos validados para fabricantes farmacéuticos con tiempo de inactividad mínimo.",
      products: [
        "Válvulas y accesorios de grado estéril",
        "Componentes mecánicos de precisión",
        "Piezas de máquinas compatibles con sala limpia",
        "Bombas dosificadoras y elementos de filtración",
      ],
      tasks: [
        "Automatización de biorreactores y control de alimentación",
        "Flujos de trabajo de validación y calibración",
        "Monitoreo ambiental de sala limpia",
        "Control de sistemas de purificación y filtración",
      ],
      solutions: [
        "PLCs y controladores validados",
        "Sensores y transmisores de grado estéril",
        "Sistemas de monitoreo ambiental y de procesos",
        "Equipos y gabinetes de grado sala limpia",
      ],
      suppliers: [
        { category: "Automatización", brands: "Siemens, Rockwell Automation, Emerson" },
        { category: "Equipos de proceso", brands: "Endress+Hauser, Mettler Toledo, Sartorius" },
        { category: "Sistemas de sala limpia", brands: "Camfil, Dexmet" },
        { category: "Control de calidad", brands: "Merck Millipore, Pall Corporation" },
      ],
      advantages: [
        "Envío prioritario para líneas de producción críticas",
        "Tiempos de entrega cortos para pedidos validados personalizados",
        "Documentación técnica completa para paquetes de validación",
        "Soporte 24/7 para emergencias y eventos de validación",
      ],
    },
  },
  "chemicals-petrochemicals": {
    en: {
      title: "Chemicals & Petrochemicals",
      description: "Comprehensive supply for chemical plants with emphasis on corrosion resistance and safety.",
      products: [
        "Chemical-resistant valves and piping",
        "Seals and gaskets for high-temperature operation",
        "Pumps for aggressive liquids and solvents",
        "Explosion-proof electrical and control components",
      ],
      tasks: [
        "Reactor automation and process control",
        "Synthesis process stabilization and dosing",
        "Safety parameter monitoring",
        "Emergency shutdown and safety system implementation",
      ],
      solutions: [
        "Corrosion-resistant valves, piping and instrumentation",
        "ESD / safety systems and interlocks",
        "Pressure, temperature and flow sensors",
        "PLC and SCADA for continuous process control",
      ],
      suppliers: [
        { category: "Automation", brands: "Siemens, Rockwell Automation, Emerson" },
        { category: "Process equipment", brands: "Endress+Hauser, Vega, KROHNE" },
        { category: "Safety", brands: "Honeywell, Yokogawa, HIMA" },
        { category: "Valves / actuators", brands: "Samson, AUMA" },
      ],
      advantages: [
        "Rapid selection of corrosion-resistant materials",
        "Custom builds for aggressive process conditions",
        "Technical consulting on material compatibility",
        "Emergency shipments to avert plant shutdowns",
      ],
    },
    de: {
      title: "Chemie & Petrochemie",
      description: "Umfassende Versorgung für Chemieanlagen mit Schwerpunkt auf Korrosionsbeständigkeit und Sicherheit.",
      products: [
        "Chemikalienbeständige Ventile und Rohrleitungen",
        "Dichtungen für Hochtemperaturbetrieb",
        "Pumpen für aggressive Flüssigkeiten und Lösungsmittel",
        "Explosionsgeschützte Elektrik und Steuerungskomponenten",
      ],
      tasks: [
        "Reaktorautomatisierung und Prozesssteuerung",
        "Stabilisierung und Dosierung von Syntheseprozessen",
        "Überwachung von Sicherheitsparametern",
        "Implementierung von Notabschaltung und Sicherheitssystemen",
      ],
      solutions: [
        "Korrosionsbeständige Ventile, Rohrleitungen und Instrumentierung",
        "ESD / Sicherheitssysteme und Verriegelungen",
        "Druck-, Temperatur- und Durchflusssensoren",
        "SPS und SCADA für kontinuierliche Prozesssteuerung",
      ],
      suppliers: [
        { category: "Automatisierung", brands: "Siemens, Rockwell Automation, Emerson" },
        { category: "Prozessausrüstung", brands: "Endress+Hauser, Vega, KROHNE" },
        { category: "Sicherheit", brands: "Honeywell, Yokogawa, HIMA" },
        { category: "Ventile / Aktoren", brands: "Samson, AUMA" },
      ],
      advantages: [
        "Schnelle Auswahl korrosionsbeständiger Materialien",
        "Kundenspezifische Konstruktionen für aggressive Prozessbedingungen",
        "Technische Beratung zur Materialkompatibilität",
        "Notlieferungen zur Vermeidung von Anlagenabschaltungen",
      ],
    },
    es: {
      title: "Química y Petroquímica",
      description: "Suministro integral para plantas químicas con énfasis en resistencia a la corrosión y seguridad.",
      products: [
        "Válvulas y tuberías resistentes a químicos",
        "Sellos y juntas para operación a alta temperatura",
        "Bombas para líquidos y solventes agresivos",
        "Componentes eléctricos y de control a prueba de explosiones",
      ],
      tasks: [
        "Automatización de reactores y control de procesos",
        "Estabilización y dosificación de procesos de síntesis",
        "Monitoreo de parámetros de seguridad",
        "Implementación de parada de emergencia y sistemas de seguridad",
      ],
      solutions: [
        "Válvulas, tuberías e instrumentación resistentes a la corrosión",
        "Sistemas ESD / seguridad e interlocks",
        "Sensores de presión, temperatura y flujo",
        "PLC y SCADA para control de procesos continuos",
      ],
      suppliers: [
        { category: "Automatización", brands: "Siemens, Rockwell Automation, Emerson" },
        { category: "Equipos de proceso", brands: "Endress+Hauser, Vega, KROHNE" },
        { category: "Seguridad", brands: "Honeywell, Yokogawa, HIMA" },
        { category: "Válvulas / actuadores", brands: "Samson, AUMA" },
      ],
      advantages: [
        "Selección rápida de materiales resistentes a la corrosión",
        "Construcciones personalizadas para condiciones de proceso agresivas",
        "Consultoría técnica sobre compatibilidad de materiales",
        "Envíos de emergencia para evitar paradas de planta",
      ],
    },
  },
  "general-manufacturing": {
    en: {
      title: "General Manufacturing",
      description: "Universal automation solutions for diverse manufacturing processes and legacy equipment.",
      products: [
        "Gearboxes and drive motors",
        "Bearings, shafts and rollers",
        "Industrial sensors and control units",
        "Tooling and machine components",
      ],
      tasks: [
        "Machine tool and CNC automation",
        "Robotization of production cells",
        "Inline quality inspection and sorting",
        "Condition monitoring of equipment",
      ],
      solutions: [
        "PLC and HMI systems",
        "Drive technology and gearboxes",
        "Machine vision and inspection systems",
        "Sensors, encoders and motion control",
      ],
      suppliers: [
        { category: "Automation", brands: "Siemens, Rockwell Automation, Schneider Electric" },
        { category: "Drive technology", brands: "SEW-EURODRIVE, Nord Drivesystems" },
        { category: "Robotics", brands: "ABB, KUKA, FANUC" },
        { category: "Sensors & Instrumentation", brands: "ifm electronic, Pepperl+Fuchs, Sick" },
      ],
      advantages: [
        "Fast scoping and selection of optimal solutions",
        "On-site technical support for setup and tuning",
        "Flexible delivery and spare-parts programs",
        "Holistic automation packages for workshops and lines",
      ],
    },
    de: {
      title: "Allgemeine Fertigung",
      description: "Universelle Automatisierungslösungen für vielfältige Fertigungsprozesse und Altanlagen.",
      products: [
        "Getriebe und Antriebsmotoren",
        "Lager, Wellen und Walzen",
        "Industriesensoren und Steuerungseinheiten",
        "Werkzeuge und Maschinenkomponenten",
      ],
      tasks: [
        "Werkzeugmaschinen- und CNC-Automatisierung",
        "Robotisierung von Produktionszellen",
        "Inline-Qualitätsprüfung und Sortierung",
        "Zustandsüberwachung von Anlagen",
      ],
      solutions: [
        "SPS- und HMI-Systeme",
        "Antriebstechnik und Getriebe",
        "Bildverarbeitungs- und Inspektionssysteme",
        "Sensoren, Encoder und Bewegungssteuerung",
      ],
      suppliers: [
        { category: "Automatisierung", brands: "Siemens, Rockwell Automation, Schneider Electric" },
        { category: "Antriebstechnik", brands: "SEW-EURODRIVE, Nord Drivesystems" },
        { category: "Robotik", brands: "ABB, KUKA, FANUC" },
        { category: "Sensoren & Instrumentierung", brands: "ifm electronic, Pepperl+Fuchs, Sick" },
      ],
      advantages: [
        "Schnelle Ermittlung und Auswahl optimaler Lösungen",
        "Technischer Support vor Ort für Einrichtung und Optimierung",
        "Flexible Liefer- und Ersatzteilprogramme",
        "Ganzheitliche Automatisierungspakete für Werkstätten und Linien",
      ],
    },
    es: {
      title: "Manufactura General",
      description: "Soluciones de automatización universales para diversos procesos de fabricación y equipos heredados.",
      products: [
        "Cajas de engranajes y motores de accionamiento",
        "Rodamientos, ejes y rodillos",
        "Sensores industriales y unidades de control",
        "Herramientas y componentes de máquinas",
      ],
      tasks: [
        "Automatización de máquinas herramienta y CNC",
        "Robotización de celdas de producción",
        "Inspección de calidad en línea y clasificación",
        "Monitoreo de condición de equipos",
      ],
      solutions: [
        "Sistemas PLC y HMI",
        "Tecnología de accionamiento y cajas de engranajes",
        "Sistemas de visión artificial e inspección",
        "Sensores, encoders y control de movimiento",
      ],
      suppliers: [
        { category: "Automatización", brands: "Siemens, Rockwell Automation, Schneider Electric" },
        { category: "Tecnología de accionamiento", brands: "SEW-EURODRIVE, Nord Drivesystems" },
        { category: "Robótica", brands: "ABB, KUKA, FANUC" },
        { category: "Sensores e Instrumentación", brands: "ifm electronic, Pepperl+Fuchs, Sick" },
      ],
      advantages: [
        "Alcance y selección rápidos de soluciones óptimas",
        "Soporte técnico in situ para configuración y ajuste",
        "Programas flexibles de entrega y repuestos",
        "Paquetes de automatización integrales para talleres y líneas",
      ],
    },
  },
  "pulp-paper-packaging": {
    en: {
      title: "Pulp & Paper / Packaging",
      description: "Mechanical and wear parts for high-load, continuous paper and packaging lines.",
      products: [
        "Rollers, bearings and housings",
        "Seals and gaskets for wet environments",
        "Cutting and forming tools",
        "Conveyor and guiding components",
      ],
      tasks: [
        "Roll and bearing replacement planning",
        "Wet-process sealing and corrosion control",
        "High-speed cutting and forming tool replacement",
        "Conveyor and material handling optimization",
      ],
      solutions: [
        "Rollers, bearings and housings",
        "Seals, gaskets and hydraulic components",
        "Cutting/forming tooling and spare kits",
        "Conveyor components and idlers",
      ],
      suppliers: [
        { category: "Mechanical & drives", brands: "SEW-EURODRIVE, Nord Drivesystems" },
        { category: "Sensors & Instrumentation", brands: "ifm electronic, Pepperl+Fuchs, Balluff" },
      ],
      advantages: [
        "Emergency replacement to keep lines running",
        "Sourcing of hard-to-find replacement parts",
        "Advice on wear-resistant upgrades",
        "Support for scheduled maintenance cycles",
      ],
    },
    de: {
      title: "Zellstoff & Papier / Verpackung",
      description: "Mechanische und Verschleißteile für hochbelastete, kontinuierliche Papier- und Verpackungslinien.",
      products: [
        "Walzen, Lager und Gehäuse",
        "Dichtungen für Nassumgebungen",
        "Schneid- und Formwerkzeuge",
        "Förder- und Führungskomponenten",
      ],
      tasks: [
        "Planung des Walzen- und Lageraustauschs",
        "Nassverfahrensabdichtung und Korrosionskontrolle",
        "Austausch von Hochgeschwindigkeits-Schneid- und Formwerkzeugen",
        "Optimierung von Förderern und Materialhandhabung",
      ],
      solutions: [
        "Walzen, Lager und Gehäuse",
        "Dichtungen und Hydraulikkomponenten",
        "Schneid-/Formwerkzeuge und Ersatzteilkits",
        "Förderkomponenten und Tragrollen",
      ],
      suppliers: [
        { category: "Mechanik & Antriebe", brands: "SEW-EURODRIVE, Nord Drivesystems" },
        { category: "Sensoren & Instrumentierung", brands: "ifm electronic, Pepperl+Fuchs, Balluff" },
      ],
      advantages: [
        "Notfallersatz um Linien am Laufen zu halten",
        "Beschaffung schwer zu findender Ersatzteile",
        "Beratung zu verschleißfesten Upgrades",
        "Unterstützung für geplante Wartungszyklen",
      ],
    },
    es: {
      title: "Pulpa y Papel / Embalaje",
      description: "Piezas mecánicas y de desgaste para líneas de papel y embalaje de alta carga y continuas.",
      products: [
        "Rodillos, rodamientos y carcasas",
        "Sellos y juntas para ambientes húmedos",
        "Herramientas de corte y formado",
        "Componentes de transporte y guía",
      ],
      tasks: [
        "Planificación de reemplazo de rodillos y rodamientos",
        "Sellado de procesos húmedos y control de corrosión",
        "Reemplazo de herramientas de corte y formado de alta velocidad",
        "Optimización de transportadores y manejo de materiales",
      ],
      solutions: [
        "Rodillos, rodamientos y carcasas",
        "Sellos, juntas y componentes hidráulicos",
        "Herramientas de corte/formado y kits de repuestos",
        "Componentes de transportador y rodillos guía",
      ],
      suppliers: [
        { category: "Mecánica y accionamientos", brands: "SEW-EURODRIVE, Nord Drivesystems" },
        { category: "Sensores e Instrumentación", brands: "ifm electronic, Pepperl+Fuchs, Balluff" },
      ],
      advantages: [
        "Reemplazo de emergencia para mantener líneas funcionando",
        "Suministro de piezas de repuesto difíciles de encontrar",
        "Asesoramiento sobre mejoras resistentes al desgaste",
        "Soporte para ciclos de mantenimiento programados",
      ],
    },
  },
  "oil-gas": {
    en: {
      title: "Oil & Gas",
      description: "Urgent, compliant supply for oil & gas with explosion-proof and high-integrity components.",
      products: [
        "High-pressure valves and manifolds",
        "Hydraulic and pneumatic actuators",
        "Filtration systems for harsh environments",
        "Explosion-proof electrical components",
      ],
      tasks: [
        "Drilling and wellhead automation",
        "Pipeline and flow monitoring",
        "Emergency shutdown and safety system operation",
        "Tank farm level and inventory control",
      ],
      solutions: [
        "Explosion-proof equipment",
        "ESD systems and SIS",
        "Telemetry RTUs and remote I/O",
        "Pressure and level measurement",
      ],
      suppliers: [
        { category: "Automation", brands: "Siemens, Schneider Electric, Emerson" },
        { category: "Oil & gas equipment", brands: "Honeywell, Yokogawa, Baker Hughes" },
        { category: "Explosion protection", brands: "Pepperl+Fuchs, R. STAHL, Eaton" },
        { category: "Sensors & Instrumentation", brands: "Endress+Hauser, Vega, KROHNE" },
      ],
      advantages: [
        "Emergency shipments for critical outages",
        "Equipment selection for extreme and remote sites",
        "Fast technical consulting for installation/commissioning",
        "Reliable delivery to offshore/onshore locations",
      ],
    },
    de: {
      title: "Öl & Gas",
      description: "Dringende, konforme Lieferung für Öl & Gas mit explosionsgeschützten und hochintegrierten Komponenten.",
      products: [
        "Hochdruckventile und Verteiler",
        "Hydraulische und pneumatische Aktoren",
        "Filtrationssysteme für raue Umgebungen",
        "Explosionsgeschützte Elektrokomponenten",
      ],
      tasks: [
        "Bohr- und Bohrkopfautomatisierung",
        "Pipeline- und Durchflussüberwachung",
        "Notabschaltung und Sicherheitssystembetrieb",
        "Tanklagerfüllstand- und Bestandskontrolle",
      ],
      solutions: [
        "Explosionsgeschützte Ausrüstung",
        "ESD-Systeme und SIS",
        "Telemetrie-RTUs und Remote-I/O",
        "Druck- und Füllstandsmessung",
      ],
      suppliers: [
        { category: "Automatisierung", brands: "Siemens, Schneider Electric, Emerson" },
        { category: "Öl- & Gasausrüstung", brands: "Honeywell, Yokogawa, Baker Hughes" },
        { category: "Explosionsschutz", brands: "Pepperl+Fuchs, R. STAHL, Eaton" },
        { category: "Sensoren & Instrumentierung", brands: "Endress+Hauser, Vega, KROHNE" },
      ],
      advantages: [
        "Notlieferungen für kritische Ausfälle",
        "Geräteauswahl für extreme und abgelegene Standorte",
        "Schnelle technische Beratung für Installation/Inbetriebnahme",
        "Zuverlässige Lieferung an Offshore-/Onshore-Standorte",
      ],
    },
    es: {
      title: "Petróleo y Gas",
      description: "Suministro urgente y conforme para petróleo y gas con componentes a prueba de explosiones y de alta integridad.",
      products: [
        "Válvulas y colectores de alta presión",
        "Actuadores hidráulicos y neumáticos",
        "Sistemas de filtración para ambientes hostiles",
        "Componentes eléctricos a prueba de explosiones",
      ],
      tasks: [
        "Automatización de perforación y cabeza de pozo",
        "Monitoreo de tuberías y flujo",
        "Operación de parada de emergencia y sistemas de seguridad",
        "Control de nivel e inventario de parques de tanques",
      ],
      solutions: [
        "Equipos a prueba de explosiones",
        "Sistemas ESD y SIS",
        "RTUs de telemetría e I/O remoto",
        "Medición de presión y nivel",
      ],
      suppliers: [
        { category: "Automatización", brands: "Siemens, Schneider Electric, Emerson" },
        { category: "Equipos de petróleo y gas", brands: "Honeywell, Yokogawa, Baker Hughes" },
        { category: "Protección contra explosiones", brands: "Pepperl+Fuchs, R. STAHL, Eaton" },
        { category: "Sensores e Instrumentación", brands: "Endress+Hauser, Vega, KROHNE" },
      ],
      advantages: [
        "Envíos de emergencia para cortes críticos",
        "Selección de equipos para sitios extremos y remotos",
        "Consultoría técnica rápida para instalación/puesta en marcha",
        "Entrega confiable a ubicaciones offshore/onshore",
      ],
    },
  },
  "mining-extraction": {
    en: {
      title: "Mining & Extraction",
      description: "Wear-resistant, heavy-duty components and hydraulic systems for extreme environments.",
      products: [
        "Wear plates and abrasion-resistant components",
        "Heavy-duty bearings and shafts",
        "Hydraulic cylinders and hoses",
        "Conveyor rollers and idlers",
      ],
      tasks: [
        "Managing abrasive wear on conveyors and crushers",
        "Hydraulic system maintenance for mobile equipment",
        "Long-distance material transport reliability",
        "Emergency replacement of heavily loaded components",
      ],
      solutions: [
        "Wear plates and abrasion-resistant assemblies",
        "Heavy-duty bearings, shafts and couplings",
        "Hydraulic cylinders, hoses and filtration systems",
        "Conveyor rollers, idlers and drive components",
      ],
      suppliers: [
        { category: "Drives & hydraulics", brands: "SEW-EURODRIVE, Nord Drivesystems" },
        { category: "Sensors & Instrumentation", brands: "ifm electronic, Pepperl+Fuchs, Sick" },
      ],
      advantages: [
        "Rapid supply for emergency repairs in remote sites",
        "Specialist sourcing for abrasion-resistant materials",
        "Field support for mobile and stationary equipment",
        "Flexible logistics for heavy and bulky parts",
      ],
    },
    de: {
      title: "Bergbau & Gewinnung",
      description: "Verschleißfeste, schwerlastfähige Komponenten und Hydrauliksysteme für extreme Umgebungen.",
      products: [
        "Verschleißplatten und abriebfeste Komponenten",
        "Schwerlastlager und Wellen",
        "Hydraulikzylinder und Schläuche",
        "Förderwalzen und Tragrollen",
      ],
      tasks: [
        "Handhabung von abrasivem Verschleiß an Förderern und Brechern",
        "Hydrauliksystemwartung für mobile Geräte",
        "Zuverlässigkeit des Langstreckenmaterialtransports",
        "Notfallersatz stark belasteter Komponenten",
      ],
      solutions: [
        "Verschleißplatten und abriebfeste Baugruppen",
        "Schwerlastlager, Wellen und Kupplungen",
        "Hydraulikzylinder, Schläuche und Filtrationssysteme",
        "Förderwalzen, Tragrollen und Antriebskomponenten",
      ],
      suppliers: [
        { category: "Antriebe & Hydraulik", brands: "SEW-EURODRIVE, Nord Drivesystems" },
        { category: "Sensoren & Instrumentierung", brands: "ifm electronic, Pepperl+Fuchs, Sick" },
      ],
      advantages: [
        "Schnelle Versorgung für Notreparaturen an abgelegenen Standorten",
        "Spezialbeschaffung für abriebfeste Materialien",
        "Feldunterstützung für mobile und stationäre Geräte",
        "Flexible Logistik für schwere und sperrige Teile",
      ],
    },
    es: {
      title: "Minería y Extracción",
      description: "Componentes resistentes al desgaste, de trabajo pesado y sistemas hidráulicos para ambientes extremos.",
      products: [
        "Placas de desgaste y componentes resistentes a la abrasión",
        "Rodamientos y ejes de trabajo pesado",
        "Cilindros y mangueras hidráulicas",
        "Rodillos y poleas de transportador",
      ],
      tasks: [
        "Gestión del desgaste abrasivo en transportadores y trituradoras",
        "Mantenimiento de sistemas hidráulicos para equipos móviles",
        "Confiabilidad del transporte de materiales a larga distancia",
        "Reemplazo de emergencia de componentes muy cargados",
      ],
      solutions: [
        "Placas de desgaste y conjuntos resistentes a la abrasión",
        "Rodamientos, ejes y acoplamientos de trabajo pesado",
        "Cilindros hidráulicos, mangueras y sistemas de filtración",
        "Rodillos de transportador, poleas y componentes de accionamiento",
      ],
      suppliers: [
        { category: "Accionamientos e hidráulica", brands: "SEW-EURODRIVE, Nord Drivesystems" },
        { category: "Sensores e Instrumentación", brands: "ifm electronic, Pepperl+Fuchs, Sick" },
      ],
      advantages: [
        "Suministro rápido para reparaciones de emergencia en sitios remotos",
        "Abastecimiento especializado para materiales resistentes a la abrasión",
        "Soporte de campo para equipos móviles y estacionarios",
        "Logística flexible para piezas pesadas y voluminosas",
      ],
    },
  },
  "water-wastewater": {
    en: {
      title: "Water & Wastewater",
      description: "Fast delivery of equipment and automation for municipal and industrial water treatment, focusing on telemetry and continuous monitoring.",
      products: [
        "Telemetry RTUs and remote I/O",
        "Level, flow and pressure sensors",
        "Pump control and soft-start systems",
        "Water quality monitoring equipment and analyzers",
      ],
      tasks: [
        "Pump station automation",
        "Wastewater treatment control",
        "Water quality parameter monitoring",
        "Remote facility telemetry and SCADA",
      ],
      solutions: [
        "Telemetry RTUs and remote I/O",
        "Level, flow and pressure sensors",
        "Pump control and soft-start systems",
        "Water quality monitoring equipment and analyzers",
      ],
      suppliers: [
        { category: "Automation", brands: "Siemens, Schneider Electric, WAGO" },
        { category: "Water treatment", brands: "Endress+Hauser, Vega, KROHNE, Xylem" },
        { category: "Pumping equipment", brands: "Grundfos, Wilo, KSB" },
        { category: "Sensors & Instrumentation", brands: "ifm electronic, Pepperl+Fuchs" },
      ],
      advantages: [
        "Emergency deliveries for critical situations",
        "Replacement of obsolete equipment without downtime",
        "Technical support for selecting compatible alternatives",
        "Flexible commercial terms for municipal projects",
      ],
    },
    de: {
      title: "Wasser & Abwasser",
      description: "Schnelle Lieferung von Ausrüstung und Automatisierung für kommunale und industrielle Wasseraufbereitung mit Fokus auf Telemetrie und kontinuierlicher Überwachung.",
      products: [
        "Telemetrie-RTUs und Remote-I/O",
        "Füllstands-, Durchfluss- und Drucksensoren",
        "Pumpensteuerung und Sanftanlaufsysteme",
        "Wasserqualitätsüberwachungsgeräte und Analysatoren",
      ],
      tasks: [
        "Pumpstationsautomatisierung",
        "Abwasseraufbereitungssteuerung",
        "Überwachung von Wasserqualitätsparametern",
        "Fernanlage-Telemetrie und SCADA",
      ],
      solutions: [
        "Telemetrie-RTUs und Remote-I/O",
        "Füllstands-, Durchfluss- und Drucksensoren",
        "Pumpensteuerung und Sanftanlaufsysteme",
        "Wasserqualitätsüberwachungsgeräte und Analysatoren",
      ],
      suppliers: [
        { category: "Automatisierung", brands: "Siemens, Schneider Electric, WAGO" },
        { category: "Wasseraufbereitung", brands: "Endress+Hauser, Vega, KROHNE, Xylem" },
        { category: "Pumpenausrüstung", brands: "Grundfos, Wilo, KSB" },
        { category: "Sensoren & Instrumentierung", brands: "ifm electronic, Pepperl+Fuchs" },
      ],
      advantages: [
        "Notlieferungen für kritische Situationen",
        "Austausch veralteter Geräte ohne Ausfallzeiten",
        "Technische Unterstützung bei der Auswahl kompatibler Alternativen",
        "Flexible kommerzielle Bedingungen für kommunale Projekte",
      ],
    },
    es: {
      title: "Agua y Aguas Residuales",
      description: "Entrega rápida de equipos y automatización para tratamiento de agua municipal e industrial, enfocado en telemetría y monitoreo continuo.",
      products: [
        "RTUs de telemetría e I/O remoto",
        "Sensores de nivel, flujo y presión",
        "Control de bombas y sistemas de arranque suave",
        "Equipos de monitoreo de calidad del agua y analizadores",
      ],
      tasks: [
        "Automatización de estaciones de bombeo",
        "Control de tratamiento de aguas residuales",
        "Monitoreo de parámetros de calidad del agua",
        "Telemetría de instalaciones remotas y SCADA",
      ],
      solutions: [
        "RTUs de telemetría e I/O remoto",
        "Sensores de nivel, flujo y presión",
        "Control de bombas y sistemas de arranque suave",
        "Equipos de monitoreo de calidad del agua y analizadores",
      ],
      suppliers: [
        { category: "Automatización", brands: "Siemens, Schneider Electric, WAGO" },
        { category: "Tratamiento de agua", brands: "Endress+Hauser, Vega, KROHNE, Xylem" },
        { category: "Equipos de bombeo", brands: "Grundfos, Wilo, KSB" },
        { category: "Sensores e Instrumentación", brands: "ifm electronic, Pepperl+Fuchs" },
      ],
      advantages: [
        "Entregas de emergencia para situaciones críticas",
        "Reemplazo de equipos obsoletos sin tiempo de inactividad",
        "Soporte técnico para seleccionar alternativas compatibles",
        "Términos comerciales flexibles para proyectos municipales",
      ],
    },
  },
};
